/**
 * Zone 1: Forest Floor Decorator
 *
 * Handles decoration for the Forest Floor zone (Zone 1)
 * Includes 19 decoration types: 14 top-side + 5 underside
 *
 * Top decorations: tiny-flower, small-frog, firefly, dewdrop, fallen-leaf,
 *   mushroom-buddy, moss-patch, acorn, small-twig, grass-tuft, tiny-snail,
 *   beetle, spider-web, butterfly
 *
 * Underside decorations: hanging-roots, hanging-branch, spider-on-web,
 *   caterpillar, shelf-mushroom
 */

import Phaser from 'phaser';
import { PlatformDecoratorBase, DecorationConfig } from './PlatformDecoratorBase';
import { SurfaceType } from '@/config/constants';
import { DecorationTextureGenerator } from '../DecorationTextureGenerator'; // PHASE 7C

export class Zone1Decorator extends PlatformDecoratorBase {
  /**
   * Get Zone 1 decoration configuration
   * PHASE 5: Reduced spawn rates for better mobile performance
   * Each decoration has 4-8 draw calls, so reducing density improves FPS
   */
  protected getDecorations(): DecorationConfig[] {
    return [
      // Top-side decorations - REDUCED spawn rates (~40% reduction)
      {
        type: 'tiny-flower',
        chance: 0.15, // Reduced from 0.25
        minWidth: 40,
        placement: 'top',
      },
      {
        type: 'small-frog',
        chance: 0.05, // Reduced from 0.08 (animated - expensive)
        allowedSurfaces: [SurfaceType.STICKY],
        minWidth: 50,
        placement: 'top',
      },
      {
        type: 'firefly',
        chance: 0.08, // Reduced from 0.15 (animated - expensive)
        minWidth: 40,
        placement: 'top',
      },
      {
        type: 'dewdrop',
        chance: 0.18, // Reduced from 0.30
        minWidth: 30,
        placement: 'top',
      },
      {
        type: 'fallen-leaf',
        chance: 0.12, // Reduced from 0.20
        allowedSurfaces: [SurfaceType.NORMAL],
        minWidth: 40,
        placement: 'top',
      },
      {
        type: 'mushroom-buddy',
        chance: 0.08, // Reduced from 0.12
        allowedSurfaces: [SurfaceType.NORMAL],
        minWidth: 50,
        placement: 'top',
      },
      // Top-side decorations - REDUCED spawn rates
      {
        type: 'moss-patch',
        chance: 0.1, // Reduced from 0.18
        minWidth: 50,
        placement: 'top',
      },
      {
        type: 'acorn',
        chance: 0.1, // Reduced from 0.15
        minWidth: 40,
        placement: 'top',
      },
      {
        type: 'small-twig',
        chance: 0.08, // Reduced from 0.12
        minWidth: 30,
        placement: 'top',
      },
      {
        type: 'grass-tuft',
        chance: 0.12, // Reduced from 0.20
        minWidth: 40,
        placement: 'top',
      },
      {
        type: 'tiny-snail',
        chance: 0.05, // Reduced from 0.08
        allowedSurfaces: [SurfaceType.STICKY, SurfaceType.NORMAL],
        minWidth: 50,
        placement: 'top',
      },
      {
        type: 'beetle',
        chance: 0.06, // Reduced from 0.10
        minWidth: 45,
        placement: 'top',
      },
      {
        type: 'spider-web',
        chance: 0.04, // Reduced from 0.07
        minWidth: 60,
        placement: 'top',
      },
      {
        type: 'butterfly',
        chance: 0.07, // Reduced from 0.12 (animated - expensive)
        minWidth: 50,
        placement: 'top',
      },
      // Underside decorations - REDUCED spawn rates (~40% reduction)
      {
        type: 'hanging-roots',
        chance: 0.2, // Reduced from 0.35
        minWidth: 40,
        placement: 'underside',
      },
      {
        type: 'hanging-branch',
        chance: 0.15, // Reduced from 0.25
        minWidth: 60,
        placement: 'underside',
      },
      {
        type: 'spider-on-web',
        chance: 0.06, // Reduced from 0.10
        minWidth: 70,
        placement: 'underside',
      },
      {
        type: 'caterpillar',
        chance: 0.08, // Reduced from 0.12
        minWidth: 50,
        placement: 'underside',
      },
      {
        type: 'shelf-mushroom',
        chance: 0.1, // Reduced from 0.15
        minWidth: 60,
        placement: 'underside',
      },
    ];
  }

  /**
   * Add a specific decoration to the platform
   * PHASE 7C: CONVERTED TO SPRITES (was Graphics)
   * Creates sprite from pre-generated texture and positions it
   */
  protected addDecoration(
    platformContainer: Phaser.GameObjects.Container,
    decorationType: string,
    _placement: 'top' | 'underside',
    width: number,
    height: number
  ): void {
    // PHASE 7C: Create sprite from pre-generated texture instead of Graphics
    const textureKey = DecorationTextureGenerator.getTextureKey(decorationType);
    const sprite = this.scene.add.sprite(0, 0, textureKey);
    sprite.setOrigin(0.5, 0.5); // Center the sprite
    platformContainer.add(sprite);

    // Calculate position based on decoration type
    // This mirrors the position logic from DecorationDrawingHelpers
    const position = this.calculateDecorationPosition(decorationType, width, height);
    sprite.setPosition(position.x, position.y);

    // Check if this decoration type is animated
    const animationType = this.getAnimationType(decorationType);
    if (animationType) {
      this.animatedElements.push({
        sprite,
        type: animationType,
        animationPhase: this.random.nextFloat(0, Math.PI * 2),
        animationSpeed: this.getAnimationSpeed(animationType),
      });
    }
  }

  /**
   * Calculate decoration position based on type
   * Mirrors position logic from DecorationDrawingHelpers
   */
  private calculateDecorationPosition(
    type: string,
    width: number,
    height: number
  ): { x: number; y: number } {
    // CRITICAL FIX: Only calculate platform-dependent positioning
    // Constant offsets are already baked into the texture (generated with width=0, height=0)
    // This prevents double-offsetting that causes decorations to hover incorrectly
    switch (type) {
      // Top-side decorations
      case 'tiny-flower':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the -10 offset
        };
      case 'small-frog':
        return {
          x: this.random.nextFloat(-width * 0.25, width * 0.25),
          y: -height / 2, // Texture has the -8 offset
        };
      case 'firefly':
        return {
          x: this.random.nextFloat(-width * 0.4, width * 0.4),
          y: -height / 2, // Texture has the random -30 to -10 offset
        };
      case 'dewdrop':
        return {
          x: this.random.nextFloat(-width * 0.4, width * 0.4),
          y: -height / 2, // Texture has the random -2 to +2 offset
        };
      case 'fallen-leaf':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the +1 offset
        };
      case 'mushroom-buddy': {
        const side = this.random.next() > 0.5 ? 1 : -1;
        return {
          x: (side * width) / 2, // Texture has the +10 offset
          y: -height / 2,
        };
      }
      case 'moss-patch':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the +1 offset
        };
      case 'acorn':
        return {
          x: this.random.nextFloat(-width * 0.35, width * 0.35),
          y: -height / 2, // Texture has the -3 offset
        };
      case 'small-twig':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the +1 offset
        };
      case 'grass-tuft':
        return {
          x: this.random.nextFloat(-width * 0.35, width * 0.35),
          y: -height / 2,
        };
      case 'tiny-snail':
        return {
          x: this.random.nextFloat(-width * 0.25, width * 0.25),
          y: -height / 2, // Texture has the -4 offset
        };
      case 'beetle':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the -3 offset
        };
      case 'spider-web': {
        const side = this.random.next() > 0.5 ? 1 : -1;
        return {
          x: (side * width) / 2, // Texture has the -5 offset
          y: -height / 2, // Texture has the -5 offset
        };
      }
      case 'butterfly':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the random -15 to -25 offset
        };

      // Underside decorations
      case 'hanging-roots':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: height / 2,
        };
      case 'hanging-branch':
        return {
          x: this.random.nextFloat(-width * 0.25, width * 0.25),
          y: height / 2,
        };
      case 'spider-on-web':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: height / 2,
        };
      case 'caterpillar':
        return {
          x: this.random.nextFloat(-width * 0.25, width * 0.25),
          y: height / 2,
        };
      case 'shelf-mushroom': {
        const side = this.random.next() > 0.5 ? 1 : -1;
        return {
          x: (side * width) / 2, // Texture has the -8 offset
          y: height / 2,
        };
      }

      default:
        return { x: 0, y: 0 };
    }
  }

  /**
   * Get animation type for decoration (if animated)
   * Returns null for static decorations
   */
  private getAnimationType(type: string): string | null {
    switch (type) {
      case 'firefly':
        return 'firefly-orbit';
      case 'small-frog':
        return 'frog-idle';
      case 'butterfly':
        return 'butterfly-flutter';
      default:
        return null;
    }
  }

  /**
   * Get animation speed for animation type
   */
  private getAnimationSpeed(type: string): number {
    switch (type) {
      case 'firefly-orbit':
        return 0.02;
      case 'frog-idle':
        return 0.03;
      case 'butterfly-flutter':
        return 0.15;
      default:
        return 0.05;
    }
  }
}
